import { useState } from "react";
import { useServerFn } from "@tanstack/react-start";
import { ArrowRight, CheckCircle2, Loader2 } from "lucide-react";
import { sendContactMessage } from "@/lib/contact.functions";
import { trackForm } from "@/lib/analytics";
import { FormPrivacyNotice, RequiredFieldHint } from "@/components/site/FormPrivacyNotice";

const inputClass =
  "mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30";

const selectClass =
  "mt-2 w-full rounded-md border border-input bg-background px-4 py-3 text-sm outline-none focus:border-ring focus:ring-2 focus:ring-ring/30";

type FieldErrors = Partial<
  Record<
    "name" | "email" | "interfaceCount" | "targetDate" | "scope" | "notes",
    string
  >
>;

/**
 * Scope option `value`s stay in English on every locale — they are part of the
 * payload the backend and the ticket mail rely on.
 */
const SCOPE_OPTIONS = {
  en: [
    { value: "", label: "Select a scope" },
    { value: "assessment", label: "Assessment & migration roadmap only" },
    { value: "full-migration", label: "Full migration: analysis, conversion, cut-over" },
    { value: "cutover-support", label: "Implementation support during cut-over" },
    { value: "post-migration", label: "Post-migration stabilisation & hypercare" },
    { value: "not-sure", label: "Not sure yet — want to discuss options" },
  ],
  tr: [
    { value: "", label: "Bir kapsam seçin" },
    { value: "assessment", label: "Yalnızca değerlendirme ve migrasyon yol haritası" },
    { value: "full-migration", label: "Tam migrasyon: analiz, dönüşüm, cut-over" },
    { value: "cutover-support", label: "Cut-over sırasında uygulama desteği" },
    { value: "post-migration", label: "Migrasyon sonrası stabilizasyon ve hypercare" },
    { value: "not-sure", label: "Henüz emin değilim — seçenekleri görüşmek isterim" },
  ],
} as const;

const COPY = {
  en: {
    heading: "Request your SAP PI/PO migration",
    intro:
      "Tell us about your SAP PI/PO landscape and migration goals. We will come back with a realistic next-step proposal.",
    name: "Name",
    company: "Company",
    email: "Email",
    interfaceCount: "Number of interfaces",
    interfacePlaceholder: "e.g. 45",
    targetDate: "Target go-live / decision date",
    targetDatePlaceholder: "e.g. Q3 2026 or 2026-09-30",
    scope: "Desired scope",
    notes: "Additional notes",
    notesPlaceholder:
      "SAP versions, PI/PO release, integration types, known constraints, preferred contact times...",
    website: "Website",
    submit: "Request migration assessment",
    sending: "Sending…",
    errName: "Please enter your name (at least 2 characters).",
    errEmail: "Please enter a valid email address.",
    errInterfaces: "Please enter a positive number of interfaces.",
    errDate: "Please select a target date or timeframe.",
    errScope: "Please select the desired migration scope.",
    toastSuccess: "Thanks — your migration enquiry has been sent.",
    toastError: "Sending failed. Please email info@oxory.net directly.",
    successHeading: "Thank you — your migration enquiry has been received.",
    successWithConfirmation:
      "Your message was delivered to our team and a confirmation copy is on its way to your email address. One of our SAP integration consultants will reply within one business day.",
    successWithoutConfirmation:
      "Your message was delivered to our team. We could not send the confirmation copy to your email address, but your enquiry is safe with us and a consultant will reply within one business day.",
    reference: "Your reference:",
    addSomething: "Need to add something? Just reply to the confirmation email or write to",
    another: "Send another enquiry",
  },
  tr: {
    heading: "SAP PI/PO migrasyonunuzu talep edin",
    intro:
      "SAP PI/PO ortamınızı ve migrasyon hedeflerinizi bize anlatın. Size gerçekçi bir sonraki adım önerisiyle döneriz.",
    name: "Ad",
    company: "Şirket",
    email: "E-posta",
    interfaceCount: "Arayüz sayısı",
    interfacePlaceholder: "örn. 45",
    targetDate: "Hedef canlıya geçiş / karar tarihi",
    targetDatePlaceholder: "örn. 2026 3. çeyrek veya 2026-09-30",
    scope: "İstenen kapsam",
    notes: "Ek notlar",
    notesPlaceholder:
      "SAP sürümleri, PI/PO release'i, entegrasyon tipleri, bilinen kısıtlar, tercih ettiğiniz iletişim saatleri...",
    website: "Web sitesi",
    submit: "Migrasyon değerlendirmesi talep edin",
    sending: "Gönderiliyor…",
    errName: "Lütfen adınızı girin (en az 2 karakter).",
    errEmail: "Lütfen geçerli bir e-posta adresi girin.",
    errInterfaces: "Lütfen pozitif bir arayüz sayısı girin.",
    errDate: "Lütfen bir hedef tarih veya zaman aralığı belirtin.",
    errScope: "Lütfen istediğiniz migrasyon kapsamını seçin.",
    toastSuccess: "Teşekkürler — migrasyon talebiniz gönderildi.",
    toastError: "Gönderim başarısız oldu. Lütfen doğrudan info@oxory.net adresine yazın.",
    successHeading: "Teşekkürler — migrasyon talebiniz bize ulaştı.",
    successWithConfirmation:
      "Mesajınız ekibimize iletildi ve onay kopyası e-posta adresinize gönderiliyor. SAP entegrasyon danışmanlarımızdan biri bir iş günü içinde yanıt verecek.",
    successWithoutConfirmation:
      "Mesajınız ekibimize iletildi. Onay kopyasını e-posta adresinize gönderemedik, ancak talebiniz bizde güvende ve bir danışmanımız bir iş günü içinde yanıt verecek.",
    reference: "Referans numaranız:",
    addSomething:
      "Eklemek istediğiniz bir şey mi var? Onay e-postasını yanıtlamanız yeterli; ya da şu adrese yazın:",
    another: "Yeni bir talep gönderin",
  },
} as const;

export function PipoMigrationEnquiryForm({
  heading,
  intro,
  formId = "pipo-migration-enquiry",
  locale,
  trackingHeading,
}: {
  heading?: string;
  intro?: string;
  formId?: string;
  /** Omit for the English site; "tr" renders the Turkish copy. */
  locale?: "tr";
  /** English label used in analytics when `heading` is localized. */
  trackingHeading?: string;
}) {
  const t = COPY[locale ?? "en"];
  const scopeOptions = SCOPE_OPTIONS[locale ?? "en"];
  const resolvedHeading = heading ?? t.heading;
  const resolvedIntro = intro ?? t.intro;
  const analyticsHeading = trackingHeading ?? resolvedHeading;
  const [form, setForm] = useState({
    name: "",
    company: "",
    email: "",
    interfaceCount: "",
    targetDate: "",
    scope: "",
    notes: "",
  });
  const [errors, setErrors] = useState<FieldErrors>({});
  const [honeypot, setHoneypot] = useState("");
  const [startedAt] = useState(() => Date.now());
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState<{ ticketRef?: string | undefined; confirmationSent: boolean } | null>(null);
  const send = useServerFn(sendContactMessage);

  const topic = "SAP PI/PO to Integration Suite migration";

  function validate(): FieldErrors {
    const next: FieldErrors = {};
    if (form.name.trim().length < 2) next.name = t.errName;
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(form.email.trim())) next.email = t.errEmail;
    if (!/^\d+$/.test(form.interfaceCount.trim()) || Number(form.interfaceCount.trim()) < 1)
      next.interfaceCount = t.errInterfaces;
    if (!form.targetDate.trim()) next.targetDate = t.errDate;
    if (!form.scope.trim()) next.scope = t.errScope;
    return next;
  }

  function composeMessage(): string {
    // Ticket body stays English so the inbox format is locale-independent.
    const scopeLabel =
      SCOPE_OPTIONS.en.find((o) => o.value === form.scope)?.label ?? form.scope;
    return [
      "SAP PI/PO to Integration Suite migration enquiry",
      "",
      `Number of interfaces: ${form.interfaceCount}`,
      `Target go-live / decision date: ${form.targetDate}`,
      `Desired scope: ${scopeLabel}`,
      "",
      "Additional notes:",
      form.notes.trim() || "—",
    ].join("\n");
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    const found = validate();
    setErrors(found);
    if (Object.keys(found).length > 0) {
      trackForm("invalid", formId, analyticsHeading, topic);
      return;
    }

    trackForm("submitted", formId, analyticsHeading, topic);
    setSending(true);
    try {
      const result = await send({
        data: {
          name: form.name,
          company: form.company,
          email: form.email,
          topic,
          message: composeMessage(),
          honeypot,
          startedAt,
          formId,
        },
      });
      const { toast } = await import("sonner");
      if (result?.ok) {
        trackForm("success", formId, analyticsHeading, topic);
        toast.success(t.toastSuccess);
        setForm({ name: "", company: "", email: "", interfaceCount: "", targetDate: "", scope: "", notes: "" });
        setErrors({});
        setSent({
          ticketRef: result.ticketRef ?? undefined,
          confirmationSent: result.confirmationSent === true,
        });
      } else {
        trackForm("error", formId, analyticsHeading, topic);
        toast.error(result?.message ?? t.toastError);
      }
    } catch (error) {
      console.error("[pipo-migration-enquiry] submit failed:", error);
      trackForm("error", formId, analyticsHeading, "network");
      (await import("sonner")).toast.error(t.toastError);
    } finally {
      setSending(false);
    }
  }

  if (sent) {
    return (
      <div
        className="rounded-lg border border-border bg-card p-8 shadow-card"
        role="status"
        aria-live="polite"
      >
        <CheckCircle2 className="size-10 text-highlight" aria-hidden="true" />
        <h2 className="mt-4 font-display text-xl font-bold">{t.successHeading}</h2>
        <p className="mt-3 text-sm text-muted-foreground">
          {sent.confirmationSent ? t.successWithConfirmation : t.successWithoutConfirmation}
        </p>
        {sent.ticketRef && (
          <p className="mt-4 text-sm font-semibold">
            {t.reference}{" "}
            <span className="rounded bg-muted px-2 py-1 font-mono text-sm">{sent.ticketRef}</span>
          </p>
        )}
        <p className="mt-4 text-sm text-muted-foreground">
          {t.addSomething}{" "}
          <a className="font-semibold underline" href="mailto:info@oxory.net">
            info@oxory.net
          </a>
          .
        </p>
        <button
          type="button"
          onClick={() => setSent(null)}
          className="mt-6 inline-flex items-center gap-2 rounded-md border border-border px-5 py-2.5 text-sm font-bold transition-colors hover:bg-muted"
        >
          {t.another}
        </button>
      </div>
    );
  }

  return (
    <form
      className="rounded-lg border border-border bg-card p-8 shadow-card"
      onSubmit={handleSubmit}
      noValidate
    >
      <h2 className="font-display text-xl font-bold">{resolvedHeading}</h2>
      <p className="mt-2 text-sm text-muted-foreground">{resolvedIntro}</p>
      <RequiredFieldHint locale={locale ?? "en"} />

      <div className="mt-6 grid gap-5 sm:grid-cols-2">
        <label className="block text-sm font-semibold">
          {t.name} <span className="text-destructive">*</span>
          <input
            className={inputClass}
            value={form.name}
            aria-invalid={Boolean(errors.name)}
            aria-describedby={errors.name ? "pipo-name-error" : undefined}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
          {errors.name && (
            <span id="pipo-name-error" className="mt-1 block text-xs font-medium text-destructive">
              {errors.name}
            </span>
          )}
        </label>
        <label className="block text-sm font-semibold">
          {t.company}
          <input
            className={inputClass}
            value={form.company}
            onChange={(e) => setForm({ ...form, company: e.target.value })}
          />
        </label>
      </div>

      <label className="mt-5 block text-sm font-semibold">
        {t.email} <span className="text-destructive">*</span>
        <input
          type="email"
          className={inputClass}
          value={form.email}
          aria-invalid={Boolean(errors.email)}
          aria-describedby={errors.email ? "pipo-email-error" : undefined}
          onChange={(e) => setForm({ ...form, email: e.target.value })}
        />
        {errors.email && (
          <span id="pipo-email-error" className="mt-1 block text-xs font-medium text-destructive">
            {errors.email}
          </span>
        )}
      </label>

      <div className="mt-5 grid gap-5 sm:grid-cols-2">
        <label className="block text-sm font-semibold">
          {t.interfaceCount} <span className="text-destructive">*</span>
          <input
            type="text"
            inputMode="numeric"
            className={inputClass}
            value={form.interfaceCount}
            placeholder={t.interfacePlaceholder}
            aria-invalid={Boolean(errors.interfaceCount)}
            aria-describedby={errors.interfaceCount ? "pipo-interfaces-error" : undefined}
            onChange={(e) => setForm({ ...form, interfaceCount: e.target.value })}
          />
          {errors.interfaceCount && (
            <span id="pipo-interfaces-error" className="mt-1 block text-xs font-medium text-destructive">
              {errors.interfaceCount}
            </span>
          )}
        </label>
        <label className="block text-sm font-semibold">
          {t.targetDate} <span className="text-destructive">*</span>
          <input
            type="text"
            className={inputClass}
            value={form.targetDate}
            placeholder={t.targetDatePlaceholder}
            aria-invalid={Boolean(errors.targetDate)}
            aria-describedby={errors.targetDate ? "pipo-date-error" : undefined}
            onChange={(e) => setForm({ ...form, targetDate: e.target.value })}
          />
          {errors.targetDate && (
            <span id="pipo-date-error" className="mt-1 block text-xs font-medium text-destructive">
              {errors.targetDate}
            </span>
          )}
        </label>
      </div>

      <label className="mt-5 block text-sm font-semibold">
        {t.scope} <span className="text-destructive">*</span>
        <select
          className={selectClass}
          value={form.scope}
          aria-invalid={Boolean(errors.scope)}
          aria-describedby={errors.scope ? "pipo-scope-error" : undefined}
          onChange={(e) => setForm({ ...form, scope: e.target.value })}
        >
          {scopeOptions.map((o) => (
            <option key={o.value} value={o.value}>
              {o.label}
            </option>
          ))}
        </select>
        {errors.scope && (
          <span id="pipo-scope-error" className="mt-1 block text-xs font-medium text-destructive">
            {errors.scope}
          </span>
        )}
      </label>

      <label className="mt-5 block text-sm font-semibold">
        {t.notes}
        <textarea
          rows={5}
          className={inputClass}
          value={form.notes}
          placeholder={t.notesPlaceholder}
          onChange={(e) => setForm({ ...form, notes: e.target.value })}
        />
      </label>


      {/* Honeypot — hidden from real users, bots tend to fill it in */}
      <div aria-hidden="true" className="absolute left-[-9999px] h-0 w-0 overflow-hidden">
        <label>
          {t.website}
          <input
            type="text"
            tabIndex={-1}
            autoComplete="off"
            value={honeypot}
            onChange={(e) => setHoneypot(e.target.value)}
          />
        </label>
      </div>

      <FormPrivacyNotice locale={locale ?? "en"} />

      <button
        type="submit"
        disabled={sending}
        className="mt-7 inline-flex items-center gap-2 rounded-md bg-primary px-6 py-3 text-sm font-bold text-primary-foreground transition-colors hover:bg-primary/90 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-highlight disabled:opacity-60"
      >
        {sending ? t.sending : t.submit}
        {sending ? <Loader2 className="size-4 animate-spin" /> : <ArrowRight className="size-4" />}
      </button>
    </form>
  );
}
